import React from 'react';
import { FormattedMessage } from 'react-intl';
import { ExtendedUser } from '../../../Types';
import Text from '@amzn/meridian/text';

// Use an enum for message IDs
enum MessageIds {
  SUCCESS_ONE = 'cs_perfportal_training_experience_assignment_success_one_user',
  SUCCESS_TWO = 'cs_perfportal_training_experience_assignment_success_two_users',
  SUCCESS_MULTIPLE = 'cs_perfportal_training_experience_assignment_success_multiple_users',
  SUCCESS_DESCRIPTION = 'cs_perfportal_training_experience_assignment_success_description',
  FAILURE_ONE = 'cs_perfportal_training_experience_assignment_failure_one_user',
  FAILURE_TWO = 'cs_perfportal_training_experience_assignment_failure_two_users',
  FAILURE_MULTIPLE = 'cs_perfportal_training_experience_assignment_failure_multiple_users',
  FAILURE_DESCRIPTION = 'cs_perfportal_training_experience_assignment_failure_description',
}

// Type the messages object
const MESSAGES: Record<MessageIds, string> = {
  [MessageIds.SUCCESS_ONE]:
    '<b>You successfully assigned a training to {username}.</b>',
  [MessageIds.SUCCESS_TWO]:
    '<b>You successfully assigned a training to {firstUsername} and {secondUsername}.</b>',
  [MessageIds.SUCCESS_MULTIPLE]:
    '<b>You successfully assigned a training to {count} associates.</b>',
  [MessageIds.SUCCESS_DESCRIPTION]:
    'It may take a few hours for this to reflect in Inspire.',
  [MessageIds.FAILURE_ONE]: '<b>Training failed to assign to {username}.</b>',
  [MessageIds.FAILURE_TWO]:
    '<b>Training failed to assign to {firstUsername} and {secondUsername}.</b>',
  [MessageIds.FAILURE_MULTIPLE]:
    '<b>Training failed to assign to {count} associates.</b>',
  [MessageIds.FAILURE_DESCRIPTION]: 'Please try again later.',
};

interface ToastMessageProps {
  users: ExtendedUser[];
  isSuccess: boolean;
}

export const ToastMessage: React.FC<ToastMessageProps> = ({
  users,
  isSuccess,
}: ToastMessageProps) => {
  // Helper function to get message ID based on count and type
  const getMessageId = count => {
    const type = isSuccess ? 'SUCCESS' : 'FAILURE';
    if (count === 1) return MessageIds[`${type}_ONE`];
    if (count === 2) return MessageIds[`${type}_TWO`];
    return MessageIds[`${type}_MULTIPLE`];
  };

  // Helper function to get description message ID
  const getDescriptionId = () =>
    MessageIds[`${isSuccess ? 'SUCCESS' : 'FAILURE'}_DESCRIPTION`];

  // Helper function to get message values based on user count
  const getMessageValues = count => {
    const baseValues = {
      b: chunks => <b>{chunks}</b>,
    };

    if (count === 1) return { ...baseValues, username: users[0].firstName };
    if (count === 2)
      return {
        ...baseValues,
        firstUsername: users[0].firstName,
        secondUsername: users[1].firstName,
      };

    // For multiple users
    return {
      ...baseValues,
      count: count,
    };
  };

  const count = users.length;
  const messageId = getMessageId(count);
  const descriptionId = getDescriptionId();
  const messageValues = getMessageValues(count);

  return (
    <>
      <Text type='b300'>
        <FormattedMessage
          id={messageId}
          defaultMessage={MESSAGES[messageId]}
          values={messageValues}
        />
      </Text>
      <Text type='b300'>
        <FormattedMessage
          id={descriptionId}
          defaultMessage={MESSAGES[descriptionId]}
        />
      </Text>
    </>
  );
};

export default ToastMessage;
